/**
 * FormBuilder 내부 형식과 API/NewCase 형식 간의 변환 유틸리티 함수들
 */

/**
 * FormBuilder 형식을 API/NewCase 형식으로 변환
 * @param {Object} formStructure - FormBuilder 내부 형식 데이터
 * @returns {Object} - API/NewCase 형식 데이터
 */
export const convertToApiFormat = (formStructure) => {
  return {
    template_id: formStructure.id || Date.now(),
    group_id: 1, // 기본값 설정
    form_name: formStructure.title,
    form_code: formStructure.id?.substring(0, 4)?.toUpperCase() || "FORM",
    form_version: "1.0",
    is_active: 1,
    pages: formStructure.pages.map((page, index) => ({
      page_number: index + 1,
      page_title: page.title,
      sections: page.sections.map((section) => ({
        section_id: section.id,
        section_title: section.title,
        // 섹션 의존성 처리 (있는 경우)
        ...(section.dependency && {
          dependsOn: section.dependency.fieldId,
          dependsOnValue: section.dependency.value,
        }),
        // rows에서 fields로 평탄화 처리
        fields: section.rows.flatMap((row) =>
          row.fields.map((field) => ({
            field_id: field.id,
            label: field.label,
            type: field.type,
            required: field.required || false,
            placeholder: field.placeholder,
            layout:
              row.layout === "two-column"
                ? field.column === "left" || field.column === "right"
                  ? "half"
                  : "full"
                : "full",
            // 필드 의존성 처리 (있는 경우)
            ...(field.dependency && {
              dependsOn: field.dependency.fieldId,
              dependsOnValue: field.dependency.value,
            }),
            // 기타 필드 속성들 복사
            ...Object.fromEntries(
              Object.entries(field).filter(
                ([key]) =>
                  ![
                    "id",
                    "type",
                    "label",
                    "required",
                    "dependency",
                    "column",
                  ].includes(key)
              )
            ),
          }))
        ),
      })),
    })),
  };
};

/**
 * API/NewCase 형식을 FormBuilder 형식으로 변환
 * @param {Object} apiData - API/NewCase 형식 데이터
 * @returns {Object} - FormBuilder 내부 형식 데이터
 */
export const convertFromApiFormat = (apiData) => {
  return {
    id: apiData.template_id?.toString(),
    title: apiData.form_name,
    description: "", // API 형식에 설명이 없으면 빈 문자열로 설정
    pages: apiData.pages.map((page) => ({
      id: `page_${page.page_number}`,
      title: page.page_title,
      sections: page.sections.map((section) => {
        // 섹션 기본 정보
        const sectionData = {
          id: section.section_id,
          title: section.section_title,
          rows: [],
        };

        // 섹션 의존성 처리
        if (section.dependsOn) {
          sectionData.dependency = {
            fieldId: section.dependsOn,
            value: section.dependsOnValue,
          };
        }

        // 필드를 행으로 그룹화
        const singleFields = section.fields.filter(
          (field) => field.layout !== "half"
        );
        const halfFields = section.fields.filter(
          (field) => field.layout === "half"
        );

        // 단일 컬럼 필드를 각각 행으로 추가
        singleFields.forEach((field) => {
          sectionData.rows.push({
            id: `row_${field.field_id}`,
            layout: "full",
            fields: [
              {
                id: field.field_id,
                type: field.type,
                label: field.label,
                required: field.required || false,
                placeholder: field.placeholder,
                // 필드 의존성 처리
                ...(field.dependsOn && {
                  dependency: {
                    fieldId: field.dependsOn,
                    value: field.dependsOnValue,
                  },
                }),
                // 기타 필드 속성들 복사
                ...Object.fromEntries(
                  Object.entries(field).filter(
                    ([key]) =>
                      ![
                        "field_id",
                        "type",
                        "label",
                        "required",
                        "layout",
                        "dependsOn",
                        "dependsOnValue",
                      ].includes(key)
                  )
                ),
              },
            ],
          });
        });

        // half 레이아웃 필드를 페어로 그룹화 (2열 레이아웃)
        for (let i = 0; i < halfFields.length; i += 2) {
          const rowId = `row_${halfFields[i].field_id}${
            halfFields[i + 1] ? `_${halfFields[i + 1].field_id}` : "_right"
          }`;

          const leftField = {
            id: halfFields[i].field_id,
            type: halfFields[i].type,
            label: halfFields[i].label,
            required: halfFields[i].required || false,
            placeholder: halfFields[i].placeholder,
            column: "left",
            // 필드 의존성 처리
            ...(halfFields[i].dependsOn && {
              dependency: {
                fieldId: halfFields[i].dependsOn,
                value: halfFields[i].dependsOnValue,
              },
            }),
            // 기타 필드 속성들 복사
            ...Object.fromEntries(
              Object.entries(halfFields[i]).filter(
                ([key]) =>
                  ![
                    "field_id",
                    "type",
                    "label",
                    "required",
                    "layout",
                    "dependsOn",
                    "dependsOnValue",
                  ].includes(key)
              )
            ),
          };

          const fields = [leftField];

          // 짝이 되는 오른쪽 필드가 있는 경우
          if (halfFields[i + 1]) {
            const rightField = {
              id: halfFields[i + 1].field_id,
              type: halfFields[i + 1].type,
              label: halfFields[i + 1].label,
              required: halfFields[i + 1].required || false,
              placeholder: halfFields[i + 1].placeholder,
              column: "right",
              // 필드 의존성 처리
              ...(halfFields[i + 1].dependsOn && {
                dependency: {
                  fieldId: halfFields[i + 1].dependsOn,
                  value: halfFields[i + 1].dependsOnValue,
                },
              }),
              // 기타 필드 속성들 복사
              ...Object.fromEntries(
                Object.entries(halfFields[i + 1]).filter(
                  ([key]) =>
                    ![
                      "field_id",
                      "type",
                      "label",
                      "required",
                      "layout",
                      "dependsOn",
                      "dependsOnValue",
                    ].includes(key)
                )
              ),
            };
            fields.push(rightField);
          }

          sectionData.rows.push({
            id: rowId,
            layout: "two-column",
            fields,
          });
        }

        return sectionData;
      }),
    })),
  };
};
